import { useMemo } from "react";
import { useAppTheme } from "../theme/useTheme";
import { REACT_SELECT_COLORS, UI_COLORS } from "../theme/colorConstants";
import { useTranslation } from "react-i18next";

/**
 * Custom hook that provides theme-aware styles and theme configuration for react-select components
 * @param {Object} options - Optional configuration
 * @param {string} options.fontFamily - Font family (default: "DM Sans")
 * @param {string} options.fontSize - Font size (default: inherit)
 * @param {string} options.fontWeight - Font weight (default: "400")
 * @param {string} options.lineHeight - Line height (default: "21px")
 * @param {number} options.zIndex - Menu z-index (default: 9999)
 * @returns {Object} Object containing `styles` and `theme` for react-select
 */
export const useReactSelectTheme = (options = {}) => {
  const { theme, isDark } = useAppTheme();
  const { t } = useTranslation();

  const {
    fontFamily = '"DM Sans"',
    fontSize = "inherit",
    fontWeight = "400",
    lineHeight = "21px",
    zIndex = 9999,
  } = options;

  const styles = useMemo(() => {
    const backgroundColor = isDark 
      ? theme.palette.background.paper 
      : REACT_SELECT_COLORS.background.light;
    const textColor = theme.palette.text.primary;
    const borderColor = isDark 
      ? REACT_SELECT_COLORS.border.dark
      : REACT_SELECT_COLORS.border.light;
    const hoverBorderColor = theme.palette.primary.main;
    const optionHoverBg = isDark 
      ? REACT_SELECT_COLORS.hover.dark
      : REACT_SELECT_COLORS.hover.light;
    const menuBg = backgroundColor;
    const placeholderColor = theme.palette.text.secondary;
    const primaryLight = theme.palette.primary.light || REACT_SELECT_COLORS.primaryLight;

    return {
      control: (styles) => ({
        ...styles,
        backgroundColor: backgroundColor,
        paddingTop: "3px",
        paddingBottom: "3px",
        borderColor: borderColor,
        boxShadow: "none",
        fontFamily: fontFamily,
        fontSize: fontSize,
        fontStyle: "normal",
        fontWeight: fontWeight,
        lineHeight: lineHeight,
        color: textColor,
        ":hover": {
          borderColor: hoverBorderColor,
        },
        ":focus": {
          borderColor: hoverBorderColor,
          boxShadow: `0 0 0 0.2rem ${primaryLight}`,
        },
      }),
      menu: (styles) => ({
        ...styles,
        backgroundColor: menuBg,
        zIndex: zIndex,
      }),
      menuList: (styles) => ({
        ...styles,
        backgroundColor: menuBg,
      }),
      option: (styles, { isFocused, isSelected, isDisabled }) => ({
        ...styles,
        fontFamily: fontFamily,
        fontSize: fontSize,
        fontStyle: "normal",
        fontWeight: fontWeight,
        lineHeight: lineHeight,
        backgroundColor: isDisabled
          ? "transparent !important" 
          : isSelected 
          ? theme.palette.primary.main 
          : isFocused 
          ? optionHoverBg 
          : "transparent",
        color: isDisabled 
          ? theme.palette.text.secondary+" !important" 
          : isSelected 
          ? REACT_SELECT_COLORS.selectedText
          : textColor,
        cursor: isDisabled ? "not-allowed !important" : "pointer !important",
        ":active": {
          backgroundColor: isDisabled 
            ? "transparent" 
            : theme.palette.primary.main,
          color: isDisabled 
            ? placeholderColor 
            : REACT_SELECT_COLORS.selectedText,
        },
      }),
      singleValue: (styles) => ({
        ...styles,
        color: textColor,
      }),
      placeholder: (styles) => ({
        ...styles,
        color: placeholderColor,
      }),
      input: (styles) => ({
        ...styles,
        color: textColor,
      }),
      multiValue: (styles) => ({
        ...styles,
        backgroundColor: isDark 
          ? REACT_SELECT_COLORS.neutral.dark.neutral8
          : REACT_SELECT_COLORS.neutral.light.neutral8,
      }),
      multiValueLabel: (styles) => ({
        ...styles,
        color: textColor,
      }),
      multiValueRemove: (styles) => ({
        ...styles,
        color: textColor,
        ":hover": {
          backgroundColor: theme.palette.error.main || "#dc3545",
          color: "#ffffff",
        },
      }),
      indicatorSeparator: (styles) => ({
        ...styles,
        backgroundColor: borderColor,
      }),
      dropdownIndicator: (styles) => ({
        ...styles,
        color: textColor,
        ":hover": {
          color: hoverBorderColor,
        },
      }),
      clearIndicator: (styles) => ({
        ...styles,
        color: textColor,
        ":hover": {
          color: theme.palette.error.main || REACT_SELECT_COLORS.error,
        },
      }),
    };
  }, [isDark, theme, fontFamily, fontSize, fontWeight, lineHeight, zIndex]);

  const selectTheme = useMemo(() => {
    return (selectTheme) => ({
      ...selectTheme,
      colors: {
        ...selectTheme.colors,
        primary: theme.palette.primary.main,
        primary75: theme.palette.primary.light || theme.palette.primary.main,
        primary50: theme.palette.primary.light || theme.palette.primary.main,
        primary25: isDark 
          ? REACT_SELECT_COLORS.neutral.dark.neutral8
          : REACT_SELECT_COLORS.hover.light,
        neutral0: isDark ? theme.palette.background.paper : REACT_SELECT_COLORS.background.light,
        neutral5: isDark ? REACT_SELECT_COLORS.neutral.dark.neutral5 : REACT_SELECT_COLORS.neutral.light.neutral5,
        neutral10: isDark ? REACT_SELECT_COLORS.neutral.dark.neutral10 : REACT_SELECT_COLORS.neutral.light.neutral10,
        neutral20: isDark ? REACT_SELECT_COLORS.neutral.dark.neutral20 : REACT_SELECT_COLORS.neutral.light.neutral20,
        neutral30: isDark ? REACT_SELECT_COLORS.neutral.dark.neutral30 : REACT_SELECT_COLORS.neutral.light.neutral30,
        neutral40: isDark ? REACT_SELECT_COLORS.neutral.dark.neutral40 : REACT_SELECT_COLORS.neutral.light.neutral40,
        neutral50: isDark ? REACT_SELECT_COLORS.neutral.dark.neutral50 : REACT_SELECT_COLORS.neutral.light.neutral50,
        neutral60: isDark ? REACT_SELECT_COLORS.neutral.dark.neutral60 : REACT_SELECT_COLORS.neutral.light.neutral60,
        neutral70: isDark ? REACT_SELECT_COLORS.neutral.dark.neutral70 : REACT_SELECT_COLORS.neutral.light.neutral70,
        neutral80: isDark ? REACT_SELECT_COLORS.neutral.dark.neutral80 : REACT_SELECT_COLORS.neutral.light.neutral80,
        neutral90: isDark ? REACT_SELECT_COLORS.neutral.dark.neutral90 : REACT_SELECT_COLORS.neutral.light.neutral90,
      },
    });
  }, [isDark, theme]);

  const selectPlaceholder = t("common.selectPlaceholder");
  const noOptionsMessage = () => t("common.noOptions");

  return {
    styles,
    theme: selectTheme,
    selectPlaceholder,
    noOptionsMessage,
  };
};

export default useReactSelectTheme;

